import { spawn } from 'node:child_process';
import * as fs from 'node:fs';
import * as os from 'node:os';
import * as path from 'node:path';
import type { MermaidThemeRegistry } from '../themes/MermaidThemeRegistry';
import type { Configuration } from '../types';
import { DiagramDebugger } from './DiagramDebugger';
import type { Logger } from './Logger';
import { MermaidAIProcessor } from './MermaidAIProcessor';

/**
 * Enterprise-grade server-side Mermaid diagram renderer with comprehensive
 * debugging and systematic error analysis.
 *
 * No hacks, no shortcuts - proper Senior SDE debugging approach.
 */
export class MermaidRenderer {
  private readonly logger: Logger;
  private readonly config: Configuration;
  private readonly themeRegistry: MermaidThemeRegistry;
  private readonly aiProcessor: MermaidAIProcessor;
  private readonly debugger: DiagramDebugger;

  constructor(config: Configuration, logger: Logger, themeRegistry: MermaidThemeRegistry) {
    this.config = config;
    this.logger = logger;
    this.themeRegistry = themeRegistry;
    this.aiProcessor = new MermaidAIProcessor(logger);
    this.debugger = new DiagramDebugger(logger);
  }

  /**
   * Pre-render all Mermaid diagrams with comprehensive debugging
   */
  async renderDiagramsInHTML(html: string): Promise<string> {
    if (!this.config.features.diagrams?.mermaid) {
      return html;
    }

    this.logger.info('[MermaidRenderer] Starting server-side rendering with debugging');

    const diagrams = this.extractMermaidDiagrams(html);

    if (diagrams.length === 0) {
      this.logger.debug('[MermaidRenderer] No Mermaid diagrams found');
      return html;
    }

    this.logger.info(
      `[MermaidRenderer] Found ${diagrams.length} diagrams - enabling comprehensive debugging`
    );

    let processedHtml = html;
    const debugInfos = [];

    // Process first 5 diagrams with extensive debugging
    for (let i = 0; i < Math.min(5, diagrams.length); i++) {
      const diagram = diagrams[i];
      const index = i + 1;

      if (!diagram) {
        this.logger.warn(`[MermaidRenderer] Diagram ${index} is undefined, skipping`);
        continue;
      }

      try {
        this.logger.info(
          `[MermaidRenderer] DEBUG MODE: Processing diagram ${index} with full analysis`
        );

        // DEBUG: Analyze original diagram
        const debugInfo = await this.debugger.debugDiagram(diagram.code, index);
        debugInfos.push(debugInfo);

        // DEBUG: Log what patterns we're trying to fix
        this.logger.info(
          `[MermaidRenderer] Diagram ${index} issues detected - Count: ${debugInfo.detectedIssues.length}`
        );
        for (const issue of debugInfo.detectedIssues) {
          this.logger.info(`[MermaidRenderer] Issue: ${issue}`);
        }

        const svg = await this.renderDiagramToSVG(diagram.code, index);
        processedHtml = processedHtml.replace(diagram.fullMatch, svg);

        this.logger.info(`[MermaidRenderer] Diagram ${index} SUCCESS`);
      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error';
        this.logger.error(`[MermaidRenderer] Diagram ${index} FAILED: ${errorMessage}`);
        this.logger.error(`[MermaidRenderer] Diagram preview: ${diagram.code.substring(0, 100)}`);
      }
    }

    // Generate comprehensive debug report
    if (debugInfos.length > 0) {
      await this.debugger.generateDebugReport(debugInfos);
    }

    // Process remaining diagrams without extensive debugging
    for (let i = 5; i < diagrams.length; i++) {
      const diagram = diagrams[i];
      const index = i + 1;

      if (!diagram) {
        this.logger.warn(`[MermaidRenderer] Diagram ${index} is undefined, skipping`);
        continue;
      }

      try {
        const svg = await this.renderDiagramToSVG(diagram.code, index);
        processedHtml = processedHtml.replace(diagram.fullMatch, svg);
      } catch (error) {
        this.logger.warn(`[MermaidRenderer] Diagram ${index} failed: ${(error as Error).message}`);
      }
    }

    return processedHtml;
  }

  /**
   * Extract diagrams from HTML
   */
  private extractMermaidDiagrams(html: string): Array<{ fullMatch: string; code: string }> {
    const diagrams: Array<{ fullMatch: string; code: string }> = [];
    const regex = /<div[^>]*class="mermaid"[^>]*>([\s\S]*?)<\/div>/gi;
    let match;

    while ((match = regex.exec(html)) !== null) {
      if (match[1] !== undefined) {
        diagrams.push({
          fullMatch: match[0],
          code: match[1].trim(),
        });
      }
    }

    return diagrams;
  }

  /**
   * Render single diagram with ERROR-DRIVEN AI approach
   */
  private async renderDiagramToSVG(diagramCode: string, debugIndex?: number): Promise<string> {
    const _tempDir = os.tmpdir();
    const _timestamp = Date.now();
    // STEP 1: Try CLI first with original code to get specific error
    const firstAttempt = await this.tryCliRenderingWithErrorCapture(diagramCode, debugIndex);

    if (firstAttempt.success && firstAttempt.svg) {
      this.logger.info(`[MermaidRenderer] Diagram ${debugIndex} SUCCESS - no AI needed`);
      return this.wrapSVGWithTheme(
        firstAttempt.svg,
        this.themeRegistry.getThemeConfig(this.config.theme)
      );
    }

    // STEP 2: CLI failed - apply static fixes first
    const staticResult = await this.aiProcessor.processAsync(diagramCode, debugIndex || 0);
    const codeToTry = staticResult.fixedCode;

    if (debugIndex && debugIndex <= 5) {
      this.logger.info(`[MermaidRenderer] Diagram ${debugIndex} failed CLI, trying static fixes`);
      this.logger.info(`[MermaidRenderer] Static fixes applied: ${staticResult.fixes.join(', ')}`);
    }

    // STEP 3: Try CLI again with static fixes
    const secondAttempt = await this.tryCliRenderingWithErrorCapture(codeToTry, debugIndex);

    if (secondAttempt.success && secondAttempt.svg) {
      this.logger.info(`[MermaidRenderer] Diagram ${debugIndex} SUCCESS with static fixes`);
      return this.wrapSVGWithTheme(
        secondAttempt.svg,
        this.themeRegistry.getThemeConfig(this.config.theme)
      );
    }

    // STEP 4: Static fixes didn't work - try ERROR-DRIVEN AI
    if (secondAttempt.error && !this.aiProcessor.isEnabled()) {
      throw new Error(`CLI failed and AI unavailable: ${secondAttempt.error}`);
    }

    if (secondAttempt.error) {
      this.logger.info(`[MermaidRenderer] Trying ERROR-DRIVEN AI for diagram ${debugIndex}`);
      this.logger.info(`[MermaidRenderer] CLI Error: ${secondAttempt.error}`);

      const aiResult = await this.processWithErrorDrivenAI(
        codeToTry,
        secondAttempt.error,
        debugIndex
      );

      if (aiResult.success) {
        this.logger.info(
          `[MermaidRenderer] Using CLEAN AI code directly - no post-processing for diagram ${debugIndex}`
        );

        // STEP 5: Try CLI with AI-corrected code (CLEAN, no post-processing)
        const thirdAttempt = await this.tryCliRenderingWithCleanCode(
          aiResult.fixedCode,
          debugIndex
        );

        if (thirdAttempt.success && thirdAttempt.svg) {
          this.logger.info(`[MermaidRenderer] Diagram ${debugIndex} SUCCESS with ERROR-DRIVEN AI!`);
          return this.wrapSVGWithTheme(
            thirdAttempt.svg,
            this.themeRegistry.getThemeConfig(this.config.theme)
          );
        } else if (thirdAttempt.error) {
          this.logger.error(
            `[MermaidRenderer] AI-corrected code still failed: ${thirdAttempt.error}`
          );
        }
      }
    }

    // All attempts failed - throw with last error
    throw new Error(secondAttempt.error || firstAttempt.error || 'All rendering attempts failed');
  }

  /**
   * Try CLI rendering and capture specific errors
   */
  private async tryCliRenderingWithErrorCapture(
    code: string,
    debugIndex?: number
  ): Promise<{
    success: boolean;
    svg?: string;
    error?: string;
  }> {
    const tempDir = os.tmpdir();
    const timestamp = Date.now();
    const inputFile = path.join(tempDir, `mermaid-${timestamp}.mmd`);
    const outputFile = path.join(tempDir, `mermaid-${timestamp}.svg`);

    try {
      // Write code to file
      fs.writeFileSync(inputFile, code, 'utf-8');

      if (debugIndex && debugIndex <= 5) {
        this.logger.info(`[MermaidRenderer] DEBUG: Trying CLI for diagram ${debugIndex}`);
        this.logger.info('=== CODE TO CLI ===');
        this.logger.info(code);
        this.logger.info('=== END CODE ===');
      }

      // Try CLI rendering
      await this.runMermaidCLI(inputFile, outputFile, code, debugIndex);

      // Success - read SVG
      const svg = fs.readFileSync(outputFile, 'utf-8');

      // Cleanup
      this.cleanupTempFiles(inputFile, outputFile);

      return { success: true, svg };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown CLI error';

      if (debugIndex && debugIndex <= 5) {
        this.logger.error(`[MermaidRenderer] CLI ERROR for diagram ${debugIndex}: ${errorMessage}`);
      }

      // Cleanup
      this.cleanupTempFiles(inputFile, outputFile);

      return { success: false, error: errorMessage };
    }
  }

  /**
   * Try CLI rendering with CLEAN AI-corrected code (no post-processing)
   */
  private async tryCliRenderingWithCleanCode(
    aiCorrectedCode: string,
    debugIndex?: number
  ): Promise<{
    success: boolean;
    svg?: string;
    error?: string;
  }> {
    const tempDir = os.tmpdir();
    const timestamp = Date.now();
    const inputFile = path.join(tempDir, `mermaid-ai-clean-${timestamp}.mmd`);
    const outputFile = path.join(tempDir, `mermaid-ai-clean-${timestamp}.svg`);

    try {
      // Write AI-corrected code DIRECTLY to file (no post-processing)
      fs.writeFileSync(inputFile, aiCorrectedCode, { encoding: 'utf-8', flag: 'w' });

      // CRITICAL: Verify file integrity - no corruption allowed
      const verifyContent = fs.readFileSync(inputFile, 'utf-8');
      if (verifyContent !== aiCorrectedCode) {
        this.logger.error(`[MermaidRenderer] FILE CORRUPTION DETECTED for diagram ${debugIndex}!`);
        this.logger.error(`AI gave us: ${aiCorrectedCode.length} chars`);
        this.logger.error(`File has: ${verifyContent.length} chars`);

        // Force clean rewrite
        fs.writeFileSync(inputFile, aiCorrectedCode, { encoding: 'utf-8', flag: 'w' });
      }

      if (debugIndex && debugIndex <= 5) {
        this.logger.info(`[MermaidRenderer] DEBUG: Trying CLEAN AI code for diagram ${debugIndex}`);
        this.logger.info('=== CLEAN AI CODE TO CLI ===');
        this.logger.info(aiCorrectedCode);
        this.logger.info('=== END CLEAN CODE ===');
      }

      // Try CLI rendering with clean AI code
      await this.runMermaidCLI(inputFile, outputFile, aiCorrectedCode, debugIndex);

      // Success - read SVG
      const svg = fs.readFileSync(outputFile, 'utf-8');

      // Cleanup
      this.cleanupTempFiles(inputFile, outputFile);

      if (debugIndex && debugIndex <= 5) {
        this.logger.info(`[MermaidRenderer] CLEAN AI CODE SUCCESS for diagram ${debugIndex}!`);
      }

      return { success: true, svg };
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown CLI error';

      if (debugIndex && debugIndex <= 5) {
        this.logger.error(
          `[MermaidRenderer] CLEAN AI CODE FAILED for diagram ${debugIndex}: ${errorMessage}`
        );
      }

      // Cleanup
      this.cleanupTempFiles(inputFile, outputFile);

      return { success: false, error: errorMessage };
    }
  }

  /**
   * Process with ERROR-DRIVEN AI using specific CLI error
   */
  private async processWithErrorDrivenAI(
    code: string,
    cliError: string,
    debugIndex?: number
  ): Promise<{
    success: boolean;
    fixedCode: string;
  }> {
    try {
      const errorDrivenPrompt = `You are a Mermaid syntax expert. Fix this diagram that failed CLI parsing:

SPECIFIC CLI ERROR:
${cliError}

FAILED MERMAID CODE:
\`\`\`mermaid
${code}
\`\`\`

MERMAID NOTE SYNTAX RULES (CRITICAL):
✅ Single line: note right of STATE: content here
✅ Multi-line:  note right of STATE
                content line 1
                content line 2
                end note

❌ WRONG: note right of STATE
          content without end note

CRITICAL INSTRUCTIONS:
1. Read the CLI error carefully - it tells you EXACTLY what's wrong
2. Fix ONLY the specific issue mentioned in the error
3. If error mentions "note right of STATE    text", choose single-line OR multi-line with "end note"
4. If error mentions "got 'DESCR'" or "Lexical error", check note syntax completion
5. ALWAYS add "end note" closure for multi-line notes
6. Preserve ALL logic and meaning - only fix the syntax issue
7. Return ONLY the corrected mermaid code

CORRECTED CODE:`;

      // Call AI with error context (using existing HTTPS method)
      const models = [
        { modelId: 'anthropic.claude-3-5-sonnet-20240620-v1:0', name: 'Claude 3.5 Sonnet' },
        { modelId: 'anthropic.claude-3-sonnet-20240229-v1:0', name: 'Claude 3 Sonnet' },
        { modelId: 'anthropic.claude-3-haiku-20240307-v1:0', name: 'Claude 3 Haiku' },
      ];

      for (const model of models) {
        try {
          const https = await import('node:https');

          const requestBody = {
            messages: [{ role: 'user', content: errorDrivenPrompt }],
            max_tokens: 2048,
            temperature: 0.05,
            anthropic_version: 'bedrock-2023-05-31',
          };

          const result = await this.makeHttpsRequest(https, model, requestBody, debugIndex);

          if (result.success) {
            this.logger.info(
              `[MermaidRenderer] ERROR-DRIVEN AI SUCCESS with ${model.name} for diagram ${debugIndex}`
            );
            return { success: true, fixedCode: result.fixedCode };
          }
        } catch (error) {
          this.logger.warn(`[MermaidRenderer] ${model.name} failed: ${error}`);
        }
      }

      return { success: false, fixedCode: code };
    } catch (error) {
      this.logger.error(`[MermaidRenderer] Error-driven AI failed: ${error}`);
      return { success: false, fixedCode: code };
    }
  }

  /**
   * Make HTTPS request to Claude model (extracted for reuse)
   */
  private async makeHttpsRequest(
    https: any,
    model: any,
    requestBody: any,
    debugIndex?: number
  ): Promise<{
    success: boolean;
    fixedCode: string;
  }> {
    return new Promise((resolve) => {
      const bodyString = JSON.stringify(requestBody);

      const options = {
        hostname: 'bedrock-runtime.us-east-1.amazonaws.com',
        port: 443,
        path: `/model/${model.modelId}/invoke`,
        method: 'POST',
        headers: {
          Authorization: `Bearer ${process.env.AWS_BEDROCK_API_KEY}`,
          'Content-Type': 'application/json',
          'Content-Length': Buffer.byteLength(bodyString),
        },
        timeout: 15000,
      };

      const req = https.request(options, (res: any) => {
        let data = '';
        res.on('data', (chunk: any) => (data += chunk));

        res.on('end', () => {
          try {
            if (res.statusCode === 200) {
              const response = JSON.parse(data);
              if (response.content?.[0]?.text) {
                const fixedCode = this.extractCodeFromAIResponse(response.content[0].text);

                if (debugIndex && debugIndex <= 5) {
                  this.logger.info(`[MermaidRenderer] AI FIXED CODE for diagram ${debugIndex}:`);
                  this.logger.info('=== AI FIXED START ===');
                  this.logger.info(fixedCode);
                  this.logger.info('=== AI FIXED END ===');
                }

                resolve({ success: true, fixedCode });
                return;
              }
            }

            this.logger.warn(`[MermaidRenderer] ${model.name} returned HTTP ${res.statusCode}`);
            resolve({ success: false, fixedCode: '' });
          } catch (_error) {
            resolve({ success: false, fixedCode: '' });
          }
        });
      });

      req.on('error', () => resolve({ success: false, fixedCode: '' }));
      req.on('timeout', () => {
        req.destroy();
        resolve({ success: false, fixedCode: '' });
      });

      req.write(bodyString);
      req.end();
    });
  }

  /**
   * Extract code from AI response
   */
  private extractCodeFromAIResponse(response: string): string {
    const codeBlockMatch = response.match(/```(?:mermaid)?\s*([\s\S]*?)\s*```/);
    if (codeBlockMatch?.[1]) {
      return codeBlockMatch[1].trim();
    }
    return response.replace(/```/g, '').trim();
  }

  /**
   * CLI execution with enhanced debugging
   */
  private async runMermaidCLI(
    inputFile: string,
    outputFile: string,
    diagramCode: string,
    debugIndex?: number
  ): Promise<void> {
    const mmdcPath = await this.resolveMmdcPath();

    return new Promise((resolve, reject) => {
      const themeConfig = this.themeRegistry.getThemeConfig(this.config.theme);

      if (debugIndex && debugIndex <= 5) {
        this.logger.info(`[MermaidRenderer] DEBUG: Executing CLI for diagram ${debugIndex}:`, {
          command: `node ${mmdcPath}`,
          args: [
            '-i',
            inputFile,
            '-o',
            outputFile,
            '-t',
            themeConfig.mermaidTheme,
            '-b',
            'transparent',
            '--quiet',
          ],
          diagramLength: diagramCode.length,
        });
      }

      const mmdc = spawn(
        'node',
        [
          mmdcPath,
          '-i',
          inputFile,
          '-o',
          outputFile,
          '-t',
          themeConfig.mermaidTheme,
          '-b',
          'transparent',
          '--quiet',
        ],
        {
          stdio: ['pipe', 'pipe', 'pipe'],
          cwd: process.cwd(),
          timeout: 15000,
          env: { ...process.env, NODE_ENV: 'production' },
        }
      );

      let stdout = '';
      let stderr = '';

      mmdc.stdout?.on('data', (data) => (stdout += data.toString()));
      mmdc.stderr?.on('data', (data) => (stderr += data.toString()));

      mmdc.on('close', (code) => {
        if (code === 0) {
          if (debugIndex && debugIndex <= 5) {
            this.logger.info(`[MermaidRenderer] DEBUG: Diagram ${debugIndex} CLI SUCCESS`);
          }
          resolve();
        } else {
          if (debugIndex && debugIndex <= 5) {
            const debugMessage = `CLI failed - exitCode: ${code}, stdout: ${stdout.trim()}, stderr: ${stderr.trim()}, file: ${inputFile}, length: ${diagramCode.length}`;
            this.logger.error(
              `[MermaidRenderer] DEBUG: Diagram ${debugIndex} CLI FAILED - ${debugMessage}`
            );
          }
          reject(new Error(`CLI failed with code ${code}: ${stderr.trim() || stdout.trim()}`));
        }
      });

      mmdc.on('error', (error) => reject(new Error(`CLI spawn failed: ${error.message}`)));
      mmdc.on('timeout', () => {
        mmdc.kill();
        reject(new Error('CLI timeout after 15 seconds'));
      });
    });
  }

  /**
   * Resolve CLI path
   */
  private async resolveMmdcPath(): Promise<string> {
    const strategies = [
      () =>
        path.join(process.cwd(), 'packages/core/node_modules/@mermaid-js/mermaid-cli/src/cli.js'),
      () => path.join(process.cwd(), 'node_modules/@mermaid-js/mermaid-cli/src/cli.js'),
      () => path.join(__dirname, '../../../node_modules/@mermaid-js/mermaid-cli/src/cli.js'),
    ];

    for (let i = 0; i < strategies.length; i++) {
      const strategy = strategies[i];
      if (strategy) {
        const mmdcPath = strategy();
        if (fs.existsSync(mmdcPath)) {
          return mmdcPath;
        }
      }
    }
    throw new Error('mmdc CLI not found');
  }

  /**
   * Wrap SVG with theme styling
   */
  private wrapSVGWithTheme(svg: string, themeConfig: any): string {
    const styles = [
      'display: flex',
      'justify-content: center',
      'align-items: center',
      `padding: ${themeConfig.container.padding}`,
      `margin: ${themeConfig.container.margin}`,
      `background: ${themeConfig.container.background}`,
      `border: 2px solid ${themeConfig.container.border}`,
      `border-radius: ${themeConfig.container.borderRadius}`,
      `box-shadow: ${themeConfig.container.shadow}`,
      `min-height: ${themeConfig.container.minHeight}`,
    ].join('; ');

    return `<div class="mermaid-rendered" style="${styles}">${svg}</div>`;
  }

  /**
   * Cleanup temporary files
   */
  private cleanupTempFiles(inputFile: string, outputFile: string): void {
    try {
      if (fs.existsSync(inputFile)) fs.unlinkSync(inputFile);
      if (fs.existsSync(outputFile)) fs.unlinkSync(outputFile);
    } catch (error) {
      this.logger.debug(`[MermaidRenderer] Cleanup failed: ${(error as Error).message}`);
    }
  }
}
